import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Lock, RotateCcw, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useSession } from "@/context/SessionContext";
import { useFetch } from "@/lib/useFetch";
import { errMsg } from "@/lib/format";
import { PageHeader, ErrorState, LoadingRows, NoAccess, Badge, EmptyState } from "@/components/common/States";

const RoleHeader = ({ r, onReset }) => (
  <th className="px-2 py-3 text-[11px] font-semibold text-center align-bottom min-w-[96px]" data-testid={`role-col-${r.key}`}>
    <div className="leading-tight">{r.label}</div>
    <div className="mt-1 flex justify-center gap-1">
      {!r.editable && <Lock className="h-3 w-3 text-muted-foreground" />}
      {r.customized && <button onClick={() => onReset(r)} className="text-[10px] text-gold inline-flex items-center gap-0.5" data-testid={`reset-role-${r.key}`}><RotateCcw className="h-3 w-3" />reset</button>}
    </div>
  </th>
);

export default function RolesPage() {
  const { can, companyId, api, reload: reloadSession } = useSession();
  const enabled = can("role.view") && !!companyId;
  const { data: roles, loading, error, reload } = useFetch("/roles", null, enabled);
  const { data: catalog } = useFetch("/meta/rbac", null, enabled);
  const [matrix, setMatrix] = useState({});
  const [dirty, setDirty] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (roles) {
      setMatrix(Object.fromEntries(roles.map((r) => [r.key, new Set(r.permissions)])));
      setDirty(new Set());
    }
  }, [roles]);

  const cols = useMemo(() => roles || [], [roles]);
  if (!can("role.view")) return <NoAccess perm="role.view" />;

  const flip = (role, perm) => {
    setMatrix((m) => {
      const s = new Set(m[role]);
      s.has(perm) ? s.delete(perm) : s.add(perm);
      return { ...m, [role]: s };
    });
    setDirty((d) => new Set(d).add(role));
  };

  const save = async () => {
    setSaving(true);
    try {
      for (const role of dirty) await api.put(`/roles/${role}`, { permissions: [...matrix[role]] }, { noBranch: true });
      toast.success("Role permissions saved");
      reload();
      reloadSession();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const reset = async (r) => {
    try {
      await api.delete(`/roles/${r.key}/override`, { noBranch: true });
      toast.success(`${r.label} reset to defaults`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="roles-page">
      <PageHeader eyebrow="Administration" title="Roles & Permissions" subtitle="Company-specific permission matrix. Every protected action is enforced by the server."
        actions={can("role.manage") && <Button onClick={save} disabled={!dirty.size || saving} data-testid="save-roles-button"><Save className="h-4 w-4 mr-1.5" />{saving ? "Saving…" : `Save changes${dirty.size ? ` (${dirty.size})` : ""}`}</Button>} />
      {!companyId ? (
        <div className="g39-card"><EmptyState title="Select a company" body="Role permissions are configured per company." /></div>
      ) : (
        <div className="g39-card overflow-hidden">
          {loading && <LoadingRows rows={8} />}
          {error && <ErrorState message={error} onRetry={reload} />}
          {roles && catalog && (
            <div className="overflow-auto max-h-[70vh]">
              <table className="w-full text-sm border-collapse" data-testid="permission-matrix">
                <thead className="sticky top-0 z-10 bg-white border-b">
                  <tr>
                    <th className="sticky left-0 bg-white px-4 py-3 text-left g39-label min-w-[220px]">Permission</th>
                    {cols.map((r) => <RoleHeader key={r.key} r={r} onReset={reset} />)}
                  </tr>
                </thead>
                <tbody>
                  {catalog.modules.map((m) => [
                    <tr key={m.key} className="bg-secondary/50">
                      <td className="sticky left-0 bg-[#F6F4EF] px-4 py-2 font-display font-semibold text-[13px]" colSpan={1}>{m.label} {m.phase > 1 && <Badge className="ml-2">Phase {m.phase}</Badge>}</td>
                      <td colSpan={cols.length} />
                    </tr>,
                    ...m.permissions.map((p) => (
                      <tr key={p} className="border-b hover:bg-accent/40">
                        <td className="sticky left-0 bg-white px-4 py-1.5 font-mono-g text-xs">{p}</td>
                        {cols.map((r) => (
                          <td key={r.key} className="text-center py-1.5">
                            <Checkbox
                              checked={!!matrix[r.key]?.has(p)}
                              disabled={!r.editable || p === "platform.manage"}
                              onCheckedChange={() => flip(r.key, p)}
                              data-testid={`perm-${r.key}-${p}`}
                            />
                          </td>
                        ))}
                      </tr>
                    )),
                  ])}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
